import firebaseApp from "services/firebase";

/**
 * True when the page is being served from localhost (or 127.0.0.1).
 * Use to gate dev-only UI like debug transcripts that should never reach prod.
 */
export const isLocalhost = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

/**
 * True when running in a development/sandbox context.
 * Used to gate dev-only features (e.g. test auth, preview routes, MFA dev hints in enrollment).
 */
export const isDevEnvironment = () => {
  const projectId = firebaseApp?.options?.projectId;
  const isDevelopmentBuild = process.env.NODE_ENV === "development";

  const sandboxProjectIds = ["hlthdsk-sandbox-2cc23", "hlthdsk-experimental"];
  const isSandboxProject = sandboxProjectIds.includes(projectId);

  return isDevelopmentBuild || isLocalhost() || isSandboxProject;
};

/**
 * Set to `true` to require MFA enrollment (onboarding, Settings, dashboard gate)
 * even on localhost — use when debugging MFA locally. Sign-in MFA challenge is unchanged.
 * Deployed sandbox already enforces MFA (see shouldSkipMfaEnrollmentUi), so this is only
 * needed for local testing.
 */
export const FORCE_MFA_ENROLLMENT_IN_DEV = false;

/**
 * Set to `true` to shrink the inactivity-logout timings while in dev (~20s idle,
 * 15s warning, 2min absolute cap) so the auto-logout flow can be tested in
 * seconds instead of the production 8-hour window. No effect in production.
 * Keep `false` on commit. Sandbox counts as dev, so a committed `true` would
 * log sandbox users out every ~35s.
 */
export const FAST_IDLE_LOGOUT_IN_DEV = false;

/**
 * When true, MFA enrollment UI and required-enrollment routing are skipped. Skips on:
 *   - LOCALHOST (fast iteration), unless {@link FORCE_MFA_ENROLLMENT_IN_DEV} is set;
 *   - any env that sets REACT_APP_DISABLE_MFA_ENROLLMENT === "true" (explicit kill switch);
 *   - any env that sets REACT_APP_INVITE_ONLY === "true" (legacy coupling, still honored).
 * Deployed non-prod envs (sandbox/staging) set none of these, so they ENFORCE MFA and
 * mirror the intended prod behavior for testing.
 *
 * Composes with roleRequiresMfa() at the gates: MFA is forced only when this returns false AND
 * the role requires it (see utils/mfaPolicy) — patients are always exempt.
 *
 * ┌──────────────────────────────────────────────────────────────────────────────────────┐
 * │ TODO(MFA-REENABLE): *** TEMPORARY *** MFA enrollment is intentionally DISABLED in        │
 * │ production right now via REACT_APP_DISABLE_MFA_ENROLLMENT=true in .env.production.        │
 * │ To turn the workforce MFA rollout back ON in prod: delete that env flag. (Also, when     │
 * │ public prod sign-ups eventually open, stop letting REACT_APP_INVITE_ONLY disable MFA —   │
 * │ it should gate sign-ups only.) Patients stay exempt regardless via mfaPolicy.            │
 * └──────────────────────────────────────────────────────────────────────────────────────┘
 */
export const shouldSkipMfaEnrollmentUi = () =>
  process.env.REACT_APP_DISABLE_MFA_ENROLLMENT === "true" ||
  process.env.REACT_APP_INVITE_ONLY === "true" ||
  (isLocalhost() && !FORCE_MFA_ENROLLMENT_IN_DEV);
